import { useEffect, useState } from "react";
import { IoIosArrowForward } from "react-icons/io";
import Select from "react-select";
import arrowup from '../Images/arrowup.svg'
import arrowdown from '../Images/arrowdown.svg'
import { GoCreditCard } from "react-icons/go";
import {
    Line,
    LineChart,
    XAxis,
    YAxis,
    ResponsiveContainer,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { MdOutlineColorLens } from "react-icons/md";
import { TbWorld } from "react-icons/tb";

const Wedding = () => {
    const [weddingData, setWeddingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const yearOpt = [
        { value: "", label: "Lifetime" },
        { value: "2025", label: "2025" },
        { value: "2026", label: "2026" },
        { value: "2027", label: "2027" },
        { value: "2028", label: "2028" },
        { value: "2029", label: "2029" },
        { value: "2030", label: "2030" },
    ];

    const monthOptions = [
        { value: "", label: "All Month" },
        { value: "1", label: "Jan" },
        { value: "2", label: "Feb" },
        { value: "3", label: "Mar" },
        { value: "4", label: "Apr" },
        { value: "5", label: "May" },
        { value: "6", label: "Jun" },
        { value: "7", label: "Jul" },
        { value: "8", label: "Aug" },
        { value: "9", label: "Sep" },
        { value: "10", label: "Oct" },
        { value: "11", label: "Nov" },
        { value: "12", label: "Dec" },
    ];


    const [selectedYear, setSelectedYear] = useState(yearOpt[0]);
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);

    const formatXAxisLabel = (value, { year, month }) => {
        const date = new Date(value);

        // Lifetime → yearly
        if (!year) {
            return date.getFullYear();
        }

        // Year selected → monthly
        if (year && !month) {
            return date.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
            });
        }

        // Year + Month → daily
        return date.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
        });
    };

    const getYearlyTicks = (trend) => {
        return trend.map((d) => new Date(d.bucket).getTime());
    };

    const isLifetime = !selectedYear;

    const TradingLineChart = () => {
        const trend = weddingData?.trend ?? [];

        if (!trend.length) {
            return (
                <div className="w-full h-56 flex items-center justify-center bg-[#FCFBFF] rounded-xl">
                    <p className="text-sm text-gray-400">No data available</p>
                </div>
            );
        }

        const values = trend.map((d) => d.count ?? 0);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const mid = (min + max) / 2;

        return (
            <div className="w-full h-56 bg-[#FCFBFF] rounded-xl p-3">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                        {/* X AXIS */}
                        <XAxis
                            dataKey="bucket"
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                            ticks={isLifetime ? getYearlyTicks(trend) : undefined}
                            padding={{ left: 20, right: 20 }}
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            tickFormatter={(value) =>
                                formatXAxisLabel(value, {
                                    year: selectedYear,
                                    month: selectedMonth,
                                })
                            }
                        />

                        {/* Hidden Y axis */}
                        <YAxis hide domain={[min, max]} ticks={[min, mid, max]} />

                        {/* Grid */}
                        <CartesianGrid
                            horizontal
                            vertical={false}
                            stroke="#EDE9FE"
                            strokeDasharray="3 3"
                        />

                        {/* Tooltip */}
                        <Tooltip
                            labelFormatter={(label) =>
                                formatXAxisLabel(label, {
                                    year: selectedYear,
                                    month: selectedMonth,
                                })
                            }
                            formatter={(value) => [value, "Count"]}
                        />

                        {/* Line */}
                        <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#7C3AED"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const handleYearChange = (option) => {
        setSelectedYear(option);
        setSelectedMonth(monthOptions[0]);
    };

    const handleMonthChange = (option) => {
        setSelectedMonth(option);
    };

    useEffect(() => {
        const token = localStorage.getItem("PhilomenaAdminToken");

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch signup data with selected range as query param

                const params = new URLSearchParams();

                if (selectedYear?.value) {
                    params.append("year", selectedYear.value);
                }

                if (selectedMonth?.value) {
                    params.append("month", selectedMonth.value);
                }

                const res1 = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/admin/get_weddings_overview?${params.toString()}`,
                    {
                        method: "GET",
                        headers: { Authorization: `Token ${token}` },
                    }
                );
                const data1 = await res1.json();
                setWeddingData(data1);

            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Failed to fetch wedding data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedYear, selectedMonth]);


    const customStyles = {
        indicatorSeparator: () => ({
            display: 'none',
        }),
        control: (base) => ({
            ...base,
            borderColor: '#F3F0F7',
            paddingTop: '0px',
            paddingBottom: '0px',
            paddingLeft: '7px',
            boxShadow: '0 1px 2px 0 #1D1C1F0D',
            borderRadius: '10px',
            borderWidth: '1px',
            width: 'max-content',
        }),
        menu: (base) => ({
            ...base,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
            color: 'black',
            padding: 10,
            cursor: 'pointer',
        }),
    };

    const pct = Math.ceil(
        weddingData?.cards?.new_weddings_today?.pct_change ?? 0
    );

    const getBadgeStyles = () => {
        if (pct > 0) return "bg-[#D1FAE4] text-[#166E3F]";
        if (pct < 0) return "bg-red-100 text-red-600";
        return "bg-gray-100 text-gray-600";
    };

    const getArrow = () => {
        if (pct > 0) return arrowup;
        if (pct < 0) return arrowdown;
        return arrowup;
    };


    if (loading) {
        return (
            <div className="text-center py-4 flex flex-col items-center justify-center h-[90vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <span className="text-gray-600">Loading...</span>
            </div>
        );
    }

    if (error) {
        return <p className="sm:py-10 py-5 text-red-600">{error}</p>;
    }

    return (
        <div className="sm:py-10 py-5 space-y-8 overflow-x-hidden">
            <div className="space-y-7">
                <p className="font-medium text-2xl">Weddings</p>

                <div className="flex sp:flex-row flex-col-reverse items-start justify-between gap-4 w-full sm:w-auto">
                    <div className="space-y-1">
                        <p className="text-[#0F132466] font-medium">Weddings Created Trend</p>
                        <div className="flex items-center gap-2">
                            <p className="sm:text-4xl text-[22px] text-black font-[450]">{weddingData?.cards?.weddings_created?.value}</p>
                            <div
                                className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium ${getBadgeStyles()}`}
                            >
                                <img
                                    src={getArrow()}
                                    alt=""
                                    className={pct === 0 ? "rotate-90 opacity-60" : ""}
                                />
                                <p>{pct}%</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Select
                            options={yearOpt}
                            value={selectedYear}
                            onChange={handleYearChange}
                            styles={customStyles}
                            className="w-full"
                            menuPlacement="auto"
                            isSearchable={false}
                        />
                        {selectedYear.value !== '' && (
                            <Select
                                options={monthOptions}
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                placeholder="Select Month"
                                styles={customStyles}
                                className="w-full"
                                menuPlacement="auto"
                                isSearchable={false}
                            />
                        )}
                    </div>
                </div>
            </div>

            <TradingLineChart />

            <div className="flex items-center justify-between gap-4 w-full sm:w-auto">
                <p className="text-[#0F132466] font-medium">Summary</p>
            </div>
            <div className="grid mc:grid-cols-3 sm:grid-cols-2 sm:justify-center sm:gap-5 gap-3 mt-7 items-start">
                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex justify-between items-center">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">Weddings Created</p>

                            <div className="flex items-center gap-2">
                                <p className="sm:text-4xl text-[22px] text-black font-[450]">{weddingData?.cards?.weddings_created?.value}</p>
                                <div
                                    className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium ${getBadgeStyles()}`}
                                >
                                    <img
                                        src={getArrow()}
                                        alt=""
                                        className={pct === 0 ? "rotate-90 opacity-60" : ""}
                                    />
                                    <p>{pct}%</p>
                                </div>

                            </div>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <MdOutlineColorLens />
                        </div>
                    </div>
                </div>

                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex justify-between items-center">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">New Weddings Today</p>

                            <div className="flex items-center gap-2">
                                <p className="sm:text-4xl text-[22px] text-black font-[450]">{weddingData?.cards?.new_weddings_today?.value}</p>
                                <div
                                    className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium ${getBadgeStyles()}`}
                                >
                                    <img
                                        src={getArrow()}
                                        alt=""
                                        className={pct === 0 ? "rotate-90 opacity-60" : ""}
                                    />
                                    <p>{pct}%</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <TbWorld />
                        </div>
                    </div>
                </div>

                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex justify-between items-center">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">Paid Weddings</p>

                            <div className="flex items-center gap-2">
                                <p className="sm:text-4xl text-[22px] text-black font-[450]">{weddingData?.cards?.paid_weddings?.value}</p>
                                <div
                                    className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium ${getBadgeStyles()}`}
                                >
                                    <img
                                        src={getArrow()}
                                        alt=""
                                        className={pct === 0 ? "rotate-90 opacity-60" : ""}
                                    />
                                    <p>{pct}%</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <GoCreditCard />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 overflow-x-auto rounded-[10px] border" style={{ WebkitOverflowScrolling: "touch" }}>
                <table className="w-full min-w-[1000px] text-nowrap border-collapse">
                    <thead>
                        <tr className="bg-[#F7F7F8] text-left text-[#0F132499] text-sm">
                            <th className="px-3 py-3 flex items-center gap-1 cursor-pointer">
                                <span>Wedding Templates</span>
                            </th>
                            <th className="px-3 py-3">Weddings Created</th>
                            <th className="px-3 py-3">Revenue Generated</th>
                            <th className="px-3 py-3">Date Added</th>
                            <th className="px-3 py-3 text-end"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {weddingData?.templates?.length > 0 ? (
                            weddingData?.templates.map((template) => {
                                return (
                                    <tr
                                        key={template.id}
                                        className="border-b hover:bg-[#F7F7F8] cursor-pointer text-sm text-[#14151A]"
                                    >
                                        <td className="px-3 py-3 flex items-center gap-2">
                                            <p className="text-[#14151A] font-medium">{template?.theme.charAt(0).toUpperCase() + template?.theme.slice(1)} Template</p>
                                        </td>
                                        <td className="px-3 py-3">
                                            <p>
                                                {template?.weddings_created ? (
                                                    template?.weddings_created
                                                ) : (
                                                    0
                                                )}
                                            </p>
                                        </td>

                                        <td className="px-3 py-3">
                                            {template?.revenue_generated ? (
                                                <p>₦{template?.revenue_generated}</p>
                                            ) : (
                                                <p>₦0</p>
                                            )}
                                        </td>
                                        <td className="px-3 py-3">
                                            {new Date(template?.date_added).toLocaleString("en-US", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </td>
                                        <td className="px-3 py-3 text-end">
                                            <p className="px-3 py-1 text-base rounded-lg">
                                                <IoIosArrowForward />
                                            </p>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan="6"
                                    className="px-3 py-3 text-center text-gray-600"
                                >
                                    No users found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Wedding;
